import api from "api";
import { useToggleState } from "hooks";
import { observer } from "mobx-react-lite";
import keys from "queries/keys";
import { useOrderQuery } from "queries/order";
import { FC } from "react";
import { CgSpinnerAlt } from "react-icons/cg";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ConfirmationModal } from "shared-widgets";
import { useStore } from "store";
import { IOrder } from "types/order";

enum OrderAction {
	REFUND,
	CONFIRM,
}

const OrderItem: FC<{ item: IOrder }> = ({ item }) => {
	const queryClient = useQueryClient();
	const confirmModalToggle = useToggleState(false);
	const cancelModalToggle = useToggleState(false);
	const { mutate: confirmOrRefundOrder, isLoading } = useMutation(
		(orderAction: OrderAction) => (orderAction === OrderAction.REFUND ? api.order.refund(item.id) : api.order.close(item.id)),
		{
			onSuccess: () => queryClient.invalidateQueries([keys.ORDER]),
		}
	);

	return (
		<>
			<ConfirmationModal
				modalToggle={confirmModalToggle}
				disabled={isLoading}
				onConfirm={() => confirmOrRefundOrder(OrderAction.CONFIRM)}
				body={"Confirm Close Order " + item.orderNumber + "?"}
			/>
			<ConfirmationModal
				modalToggle={cancelModalToggle}
				disabled={isLoading}
				onConfirm={() => confirmOrRefundOrder(OrderAction.REFUND)}
				body={"Confirm Refund Order " + item.orderNumber + "?"}
			/>
			<div className="flex flex-col justify-between shadow p-5 rounded-lg bg-gray-primary relative mb-5">
				<div>
					<span className="text-xl font-bold">Order No: </span>
					<span className="text-lg">{item.orderNumber}</span>
					<ul className="mt-2 mb-5 list-disc">
						{item.transactions.map((transaction) => (
							<li className="flex justify-between items-baseline text-secondary" key={transaction.id}>
								<div className="flex flex-col">
									<span className="text-lg">{transaction.menuItemName}</span>
									{Object.entries(transaction.options).map((option) => (
										<span className="text-sm ml-3 text-secondary" key={option[0]}>
											- {option[0]}: {option[1].name}
										</span>
									))}
								</div>
								<span className="text-md">x{transaction.quantity}</span>
							</li>
						))}
					</ul>
				</div>
				<div className="flex flex-col justify-center items-center space-y-2">
					<button className="px-3 w-full py-5 bg-green-500 rounded" onClick={confirmModalToggle.on}>
						Close
					</button>
					<button className="px-3 w-full py-5 bg-red-500 rounded" onClick={cancelModalToggle.on}>
						Refund
					</button>
				</div>
			</div>
		</>
	);
};

const OrderItems: FC = observer(() => {
	const {
		query: { data: orders, isLoading },
	} = useOrderQuery("open");

	return (
		<div className="h-full w-full grid grid-cols-3 xl:grid-cols-5 gap-3">
			{isLoading ? (
				<CgSpinnerAlt className="text-gray-500 w-10 h-10 animate-spin" />
			) : orders?.total ? (
				orders.items.map((item) => <OrderItem key={item.id} item={item} />)
			) : (
				<span className="text-gray-500">No Orders Yet</span>
			)}
		</div>
	);
});

const Orders: FC = () => {
	const { order } = useStore();
	const confirmCloseAllOrdersConfirmationModalToggle = useToggleState(false);

	const { mutate: closeAllOrders, isLoading } = useMutation(api.order.closeAll);

	return (
		<>
			<ConfirmationModal
				onConfirm={closeAllOrders}
				body="Confirm Close All Orders?"
				modalToggle={confirmCloseAllOrdersConfirmationModalToggle}
			/>
			<div className="h-full gap-x-5 pl-7 pr-5">
				<div className="mb-3">
					<button className="btn-primary px-2 py-1" onClick={confirmCloseAllOrdersConfirmationModalToggle.on} disabled={isLoading}>
						Close All
					</button>
				</div>
				<div className="w-full h-[calc(100vh-100px)] max-h-[calc(100vh-100px)] overflow-auto">
					<OrderItems />
				</div>
			</div>
		</>
	);
};

export default Orders;
